"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, FilterX, LogIn, Mail, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { EmptyState } from "@/components/ds/empty-state";
import { ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { tableHeaderSearchInputClass, ThemedSelect, themedSelectMenuClass, themedSelectTriggerClass } from "@/components/ui-system/select";
import { isAppHttpError } from "@/lib/api/client";
import { impersonateUser } from "@/modules/auth/api";
import { createOrganization, listIndianStates, listOrganizations, listOrganizationTags } from "@/lib/api/endpoints/organizations";
import { listOrganizationLocations } from "@/lib/api/endpoints/organization-locations";
import { listTenants, uploadTenantLogo } from "@/lib/api/endpoints/tenants";
import { listRoles, type RoleDto } from "@/lib/api/endpoints/roles";
import { createUser, deleteUser, listUsers, resendInvitation, updateUser } from "@/lib/api/endpoints/users";
import type { TenantUserRole, UserDto, UserUpdateBody } from "@/lib/api/types/enterprise";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

const ROLE_OPTIONS: { value: TenantUserRole; label: string }[] = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "distributor", label: "Distributor" },
  { value: "user", label: "User" },
];

const SELECT_TRIGGER_CLASS = themedSelectTriggerClass;
const SELECT_MENU_CLASS = themedSelectMenuClass;
const USERS_PAGE_SIZE = 25;
const USERS_PAGE_SIZE_OPTIONS = [10, 25, 50];

function roleLabel(role: string | null | undefined): string {
  if (!role) {
    return "Unknown";
  }
  return ROLE_OPTIONS.find((o) => o.value === role)?.label ?? role;
}

function errorDetail(err: unknown): string {
  if (isAppHttpError(err)) return err.message;
  if (err instanceof Error) return err.message;
  return "Request failed";
}

type FormState = {
  email: string;
  phone: string;
  user_code_year_range: string;
  user_code_suffix: string;
  tags: string[];
  new_tag: string;
  name: string;
  role: TenantUserRole;
  onboarding_mode: "invitation" | "manual";
  password: string;
  confirm_password: string;
  password_acknowledged: boolean;
  role_id: string;
  tenant_ids: string[];
  auto_create_workspace: boolean;
  workspace_company_name: string;
  workspace_logo_file: File | null;
  organization_id: string;
  location_id: string;
  admin_user_id: string;
  distributor_user_id: string;
  storage_limit_gb: string;
  is_active: boolean;
};

type NewOrganizationFormState = {
  name: string;
  contact_email: string;
  phone: string;
  website: string;
  tag_ids: string[];
  new_tags: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state_id: string;
  pincode: string;
  note: string;
};

const emptyForm = (defaults: Partial<FormState> = {}): FormState => ({
  email: "",
  phone: "",
  user_code_year_range: defaultUserCodeYearRange(),
  user_code_suffix: "",
  tags: [],
  new_tag: "",
  name: "",
  role: "user",
  onboarding_mode: "invitation",
  password: "",
  confirm_password: "",
  password_acknowledged: false,
  role_id: "",
  tenant_ids: [],
  auto_create_workspace: false,
  workspace_company_name: "",
  workspace_logo_file: null,
  organization_id: "",
  location_id: "",
  admin_user_id: "",
  distributor_user_id: "",
  storage_limit_gb: "",
  is_active: true,
  ...defaults,
});

function defaultUserCodeYearRange(): string {
  const now = new Date();
  const currentYear = String(now.getFullYear()).slice(-2);
  const nextYear = String(now.getFullYear() + 1).slice(-2);
  return `${currentYear}-${nextYear}`;
}

function composeUserCode(yearRange: string, suffix: string): string | null {
  const normalizedRange = yearRange.trim();
  const normalizedSuffix = suffix.trim().toUpperCase();
  if (!normalizedRange || !normalizedSuffix) {
    return null;
  }
  return `DT/${normalizedRange}/${normalizedSuffix}`;
}

function parseUserCode(userCode: string | null | undefined): { yearRange: string; suffix: string } {
  if (!userCode) {
    return { yearRange: defaultUserCodeYearRange(), suffix: "" };
  }
  const match = userCode.trim().toUpperCase().match(/^DT\/([^/]+)\/([^/]+)$/);
  if (!match) {
    return { yearRange: defaultUserCodeYearRange(), suffix: "" };
  }
  const [, yearRange = defaultUserCodeYearRange(), suffix = ""] = match;
  return { yearRange, suffix };
}

function normalizeUserTag(tag: string): string {
  return tag.trim().replace(/\s+/g, " ").slice(0, 64);
}

function nextAvailableUserCodeSuffix(users: UserDto[], yearRange: string): string {
  const normalizedRange = yearRange.trim();
  let maxValue = 0;
  for (const user of users) {
    const parsed = parseUserCode(user.user_code);
    if (parsed.yearRange !== normalizedRange) {
      continue;
    }
    if (!/^\d+$/.test(parsed.suffix)) {
      continue;
    }
    maxValue = Math.max(maxValue, Number(parsed.suffix));
  }
  return String(maxValue + 1);
}

const emptyNewOrganizationForm = (): NewOrganizationFormState => ({
  name: "",
  contact_email: "",
  phone: "",
  website: "",
  tag_ids: [],
  new_tags: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state_id: "",
  pincode: "",
  note: "",
});

const S = {
  page: {
    display: "grid",
    gap: "20px",
    color: "#1f2937",
    fontFamily: "'Poppins', 'Segoe UI', system-ui, sans-serif",
  } as React.CSSProperties,
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap" as const,
    padding: "14px 0 4px",
  } as React.CSSProperties,
  search: {
    height: "40px",
    minWidth: "240px",
    flex: 1,
    padding: "0 12px",
    borderRadius: "14px",
    border: "1px solid var(--table-border)",
    background: "var(--card)",
    outline: "none",
    fontSize: "13px",
  } as React.CSSProperties,
  select: {
    height: "40px",
    minWidth: "160px",
    padding: "0 12px",
    borderRadius: "14px",
    border: "1px solid var(--table-border)",
    background: "var(--card)",
    outline: "none",
    fontSize: "13px",
    cursor: "pointer",
  } as React.CSSProperties,
  tableWrap: {
    overflowX: "auto" as const,
    borderRadius: "20px",
    border: "1px solid var(--table-border)",
    background: "var(--card)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  } as React.CSSProperties,
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    padding: "16px 18px",
    borderBottom: "1px solid var(--table-border)",
    background: "var(--muted-bg)",
  } as React.CSSProperties,
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
  } as React.CSSProperties,
  thead: {
    background: "var(--muted-bg)",
  } as React.CSSProperties,
  th: {
    padding: "12px 14px",
    textAlign: "left" as const,
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    color: "var(--muted)",
    borderBottom: "1px solid var(--table-border)",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  td: {
    padding: "13px 14px",
    borderBottom: "1px solid var(--table-border)",
    verticalAlign: "middle" as const,
    fontSize: "13px",
  } as React.CSSProperties,
  muted: {
    color: "var(--muted)",
  } as React.CSSProperties,
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    borderRadius: "999px",
    border: "1px solid transparent",
    fontSize: "11px",
    fontWeight: 700,
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  } as React.CSSProperties,
  btn: {
    height: "36px",
    padding: "0 14px",
    borderRadius: "10px",
    border: "1px solid var(--table-border)",
    background: "var(--card)",
    color: "var(--foreground)",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.16s ease",
  } as React.CSSProperties,
  btnPrimary: {
    height: "36px",
    padding: "0 14px",
    borderRadius: "10px",
    border: "1px solid var(--btn-primary-to)",
    background: "var(--btn-primary-to)",
    color: "var(--btn-primary-fg)",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.16s ease",
  } as React.CSSProperties,
  linkBtn: {
    height: "34px",
    padding: "0 10px",
    borderRadius: "10px",
    border: "1px solid rgba(59,130,246,0.18)",
    background: "rgba(59,130,246,0.10)",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  } as React.CSSProperties,
  dangerBtn: {
    border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)",
    background: "#fff5f5",
    color: "var(--error)",
  } as React.CSSProperties,
  disabledBtn: {
    opacity: 0.45,
    cursor: "not-allowed",
  } as React.CSSProperties,
  empty: {
    padding: "34px 16px",
    textAlign: "center" as const,
    color: "var(--muted)",
    fontSize: "13px",
  } as React.CSSProperties,
  modalBackdrop: {
    position: "fixed" as const,
    inset: 0,
    zIndex: 50,
    background: "rgba(15, 23, 42, 0.52)",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  } as React.CSSProperties,
  modal: {
    width: "100%",
    maxWidth: "560px",
    maxHeight: "90vh",
    overflow: "auto",
    background: "var(--card)",
    borderRadius: "24px",
    border: "1px solid var(--table-border)",
    boxShadow: "0 24px 60px -32px rgba(15,23,42,0.24)",
  } as React.CSSProperties,
  modalHead: {
    padding: "18px 22px",
    borderBottom: "1px solid var(--table-border)",
    background: "var(--muted-bg)",
  } as React.CSSProperties,
  modalTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 700,
    color: "var(--foreground)",
  } as React.CSSProperties,
  modalSub: {
    margin: "4px 0 0",
    fontSize: "12px",
    color: "var(--muted)",
  } as React.CSSProperties,
  modalBody: {
    padding: "22px",
    display: "grid",
    gap: "18px",
  } as React.CSSProperties,
  field: {
    display: "grid",
    gap: "8px",
  } as React.CSSProperties,
  label: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#374151",
  } as React.CSSProperties,
  input: {
    height: "42px",
    padding: "0 12px",
    borderRadius: "12px",
    border: "1px solid var(--table-border)",
    background: "var(--card)",
    outline: "none",
    fontSize: "13px",
  } as React.CSSProperties,
  selectField: {
    height: "42px",
    padding: "0 12px",
    borderRadius: "12px",
    border: "1px solid var(--table-border)",
    background: "var(--card)",
    outline: "none",
    fontSize: "13px",
    cursor: "pointer",
  } as React.CSSProperties,
  checkbox: {
    width: "16px",
    height: "16px",
  } as React.CSSProperties,
  modalFooter: {
    padding: "18px 22px",
    borderTop: "1px solid var(--table-border)",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,
  error: {
    color: "var(--error)",
    background: "color-mix(in srgb, var(--error) 12%, var(--card))",
    border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)",
    borderRadius: "10px",
    padding: "10px 12px",
    fontSize: "12px",
  } as React.CSSProperties,
  sectionCard: {
    display: "grid",
    gap: "14px",
    padding: "16px",
    border: "1px solid var(--table-border)",
    borderRadius: "16px",
    background: "var(--muted-bg)",
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    color: "var(--muted)",
  } as React.CSSProperties,
  checkboxCard: {
    border: "1px solid var(--table-border)",
    borderRadius: "12px",
    background: "var(--card)",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  } as React.CSSProperties,
};

function tone(role: string): React.CSSProperties {
  switch (role) {
    case "super_admin":
      return { background: "var(--accent-primary-muted)", color: "var(--accent-primary)", borderColor: "var(--accent-primary-soft)" };
    case "admin":
      return { background: "var(--semantic-success-surface)", color: "var(--semantic-success)", borderColor: "var(--semantic-success-border)" };
    case "distributor":
      return { background: "var(--semantic-warning-surface)", color: "var(--semantic-warning)", borderColor: "var(--semantic-warning-border)" };
    default:
      return { background: "var(--surface-secondary)", color: "var(--muted)", borderColor: "var(--card-border)" };
  }
}

function accountStatusTone(status: UserDto["account_status"]): React.CSSProperties {
  switch (status) {
    case "active":
      return { background: "var(--semantic-success-surface)", color: "var(--semantic-success)", borderColor: "var(--semantic-success-border)" };
    case "invited":
      return { background: "var(--accent-primary-muted)", color: "var(--accent-primary)", borderColor: "var(--accent-primary-soft)" };
    case "disabled":
      return { background: "var(--semantic-error-surface)", color: "var(--semantic-error)", borderColor: "var(--semantic-error-border)" };
    default:
      return { background: "var(--surface-secondary)", color: "var(--muted)", borderColor: "var(--card-border)" };
  }
}

function accountStatusLabel(user: UserDto): string {
  if (!user.is_active) return "Disabled";
  if (user.account_status === "invited") return "Invited";
  if (user.account_status === "active") return "Active";
  if (user.account_status === "disabled") return "Disabled";
  return user.account_status;
}

function canCreateRole(
  actor: { role?: string; accountType?: string | null } | null | undefined,
  role: TenantUserRole,
): boolean {
  if (actor?.role === "super_admin" || actor?.accountType === "platform") return role !== "super_admin";
  const actorRole = actor?.role;
  if (actorRole === "admin") return role === "admin" || role === "distributor" || role === "user";
  if (actorRole === "distributor") return role === "distributor" || role === "user";
  if (actorRole === "user") return role === "user";
  return false;
}

function canEditUser(actor: { id?: string; role?: string; accountType?: string | null } | null | undefined, target: UserDto): boolean {
  if (!actor?.role) return false;
  if (actor.role === "super_admin" || actor.accountType === "platform") return true;
  if (actor.role === "admin") {
    if (target.id === actor.id) return true;
    return target.role !== "super_admin";
  }
  if (actor.role === "distributor") {
    if (target.id === actor.id) return true;
    return target.role === "user" && target.distributor_user_id === actor.id;
  }
  return false;
}

function tenantSummary(user: UserDto): string {
  if (user.tenants?.length) {
    return user.tenants.map((tenant) => tenant.name).join(", ");
  }
  if (user.tenant_id) {
    return user.tenant_id;
  }
  return "—";
}

export function UsersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const dialogs = useAppDialogs();
  const me = useAuthStore((s) => s.user);
  const activeTenantId = useAuthStore((s) => s.tenantId);
  const { permissionMap } = useEffectivePermissions();
  const canAccess = hasModulePermission(me, permissionMap, "users", "view");
  const canCreateUsers = hasModulePermission(me, permissionMap, "users", "create");
  const canEditUsers = hasModulePermission(me, permissionMap, "users", "edit");
  const canDeleteUsers = hasModulePermission(me, permissionMap, "users", "delete");
  const isSuperAdmin = me?.role === "super_admin";
  const isGlobalCustomerManager = isSuperAdmin || me?.accountType === "platform";
  const isDistributor = me?.role === "distributor";

  const [dialog, setDialog] = useState<"create" | "edit" | null>(null);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"" | TenantUserRole>("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(USERS_PAGE_SIZE);
  const [assignOrgTarget, setAssignOrgTarget] = useState<{ user: UserDto; tenantId: string } | null>(null);
  const [assignOrgMode, setAssignOrgMode] = useState<"existing" | "new">("existing");
  const [assignOrgId, setAssignOrgId] = useState("");
  const [assignLocationId, setAssignLocationId] = useState("");
  const [newOrgForm, setNewOrgForm] = useState<NewOrganizationFormState>(emptyNewOrganizationForm());
  const [assignOrgError, setAssignOrgError] = useState<string | null>(null);

  const effectiveWorkspaceFilter = isGlobalCustomerManager ? (activeTenantId ?? "all") : (activeTenantId ?? "");
  const isAllWorkspacesScope = isGlobalCustomerManager && !activeTenantId;
  const autoCreateWorkspaceEnabled = dialog === "create" && isGlobalCustomerManager && form.role === "admin";
  const workspaceAssignmentRequired = !(autoCreateWorkspaceEnabled && form.auto_create_workspace);
  const formRoleTenantId = workspaceAssignmentRequired && form.tenant_ids.length === 1 ? form.tenant_ids[0] : "";
  const editOrganizationId = dialog === "edit"
    ? isGlobalCustomerManager
      ? form.organization_id
      : form.organization_id || me?.organizationId || ""
    : "";

  const q = useQuery({
    queryKey: ["users", effectiveWorkspaceFilter || "none", search, roleFilter, page, pageSize],
    queryFn: () =>
      listUsers(
        isAllWorkspacesScope ? null : (effectiveWorkspaceFilter || null),
        isAllWorkspacesScope ? "all" : null,
        {
          search: search || null,
          role: roleFilter || null,
          limit: pageSize,
          offset: page * pageSize,
        },
      ),
    enabled: canAccess && (isAllWorkspacesScope || Boolean(effectiveWorkspaceFilter)),
    placeholderData: (previousData) => previousData,
  });
  const tenantsQuery = useQuery({
    queryKey: ["tenant-memberships", me?.id ?? ""],
    queryFn: () => listTenants(),
    enabled: Boolean(me?.id) && canAccess,
  });
  const organizationsQuery = useQuery({
    queryKey: ["organizations", "global", "user-edit"],
    queryFn: () => listOrganizations(),
    enabled: dialog === "edit" && isGlobalCustomerManager,
  });
  const directoryUsersQuery = useQuery({
    queryKey: ["users", "directory-options", effectiveWorkspaceFilter || "none"],
    queryFn: () =>
      listUsers(
        isAllWorkspacesScope ? null : (effectiveWorkspaceFilter || null),
        isAllWorkspacesScope ? "all" : null,
        { limit: 500, offset: 0 },
      ),
    enabled: Boolean(dialog) && canAccess && (isAllWorkspacesScope || Boolean(effectiveWorkspaceFilter)),
  });
  const editLocationsQuery = useQuery({
    queryKey: ["organization-locations", editOrganizationId || "none", "user-edit"],
    queryFn: () => {
      if (!editOrganizationId) {
        return { items: [], total: 0 };
      }
      return listOrganizationLocations(editOrganizationId);
    },
    enabled: dialog === "edit" && Boolean(editOrganizationId),
  });
  const rolesQuery = useQuery({
    queryKey: ["roles", formRoleTenantId || "none", "user-form"],
    queryFn: () => listRoles(formRoleTenantId),
    enabled: Boolean(formRoleTenantId) && !isGlobalCustomerManager,
  });
  const assignOrganizationsQuery = useQuery({
    queryKey: ["organizations", "global", "assign-user"],
    queryFn: () => listOrganizations(),
    enabled: Boolean(assignOrgTarget),
  });
  const statesQuery = useQuery({
    queryKey: ["reference", "indian-states"],
    queryFn: listIndianStates,
    staleTime: 24 * 60 * 60 * 1000,
  });
  const organizationTagsQuery = useQuery({
    queryKey: ["organizations", "tags", "assign-user"],
    queryFn: listOrganizationTags,
    enabled: Boolean(assignOrgTarget) && isGlobalCustomerManager,
    staleTime: 60_000,
  });
  const assignLocationsQuery = useQuery({
    queryKey: ["organization-locations", assignOrgId || "none", "assign-user"],
    queryFn: () => {
      if (!assignOrgId) {
        return { items: [], total: 0 };
      }
      return listOrganizationLocations(assignOrgId);
    },
    enabled: assignOrgMode === "existing" && Boolean(assignOrgId),
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const created = await createUser(
        {
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          user_code: isGlobalCustomerManager ? composeUserCode(form.user_code_year_range, form.user_code_suffix) : null,
          tags: isGlobalCustomerManager ? form.tags : [],
          name: form.name.trim(),
          role: form.role,
          onboarding_mode: form.onboarding_mode,
          password: form.onboarding_mode === "manual" ? form.password : null,
          confirm_password: form.onboarding_mode === "manual" ? form.confirm_password : null,
          password_acknowledged: form.onboarding_mode === "manual" ? form.password_acknowledged : false,
          role_id: form.role_id || null,
          tenant_ids: workspaceAssignmentRequired ? form.tenant_ids : [],
          auto_create_workspace: autoCreateWorkspaceEnabled ? form.auto_create_workspace : false,
          workspace_company_name:
            autoCreateWorkspaceEnabled && form.auto_create_workspace
              ? form.workspace_company_name.trim() || form.name.trim()
              : null,
          organization_id: null,
          admin_user_id: form.role === "user" ? form.admin_user_id || null : null,
          distributor_user_id: form.role === "user" ? (isDistributor ? me!.id : form.distributor_user_id || null) : null,
          storage_limit_gb: isGlobalCustomerManager && form.role === "admin" ? Number(form.storage_limit_gb) : null,
          is_active: form.is_active,
        },
        activeTenantId
      );
      return {
        created,
        workspaceLogoFile: autoCreateWorkspaceEnabled && form.auto_create_workspace ? form.workspace_logo_file : null,
      };
    },
    onSuccess: async ({ created, workspaceLogoFile }) => {
      const assignmentTenantId = created.tenant_id ?? created.tenant_ids?.[0] ?? form.tenant_ids[0] ?? activeTenantId ?? "";
      if (workspaceLogoFile && assignmentTenantId) {
        try {
          await uploadTenantLogo(assignmentTenantId, workspaceLogoFile);
          await qc.invalidateQueries({ queryKey: ["tenant-memberships"] });
          await qc.invalidateQueries({ queryKey: ["tenants-management"] });
        } catch (error) {
          await dialogs.alert({
            title: "Admin created, logo not uploaded",
            description: `${errorDetail(error)} You can upload the workspace logo from Workspaces.`,
            tone: "danger",
          });
        }
      }
      await qc.invalidateQueries({ queryKey: ["users"] });
      setDialog(null);
      if (assignmentTenantId) {
        setAssignOrgTarget({ user: created, tenantId: assignmentTenantId });
        setAssignOrgMode("existing");
        setAssignOrgId(!isGlobalCustomerManager ? created.organization_id ?? me?.organizationId ?? "" : "");
        setAssignLocationId("");
        setNewOrgForm(emptyNewOrganizationForm());
        setAssignOrgError(null);
      }
      setForm(emptyForm());
      setEditing(null);
      setFormError(null);
    },
    onError: (e) => setFormError(errorDetail(e)),
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("No user");
      const body: UserUpdateBody = {
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
        role: form.role,
        role_id: form.role_id || null,
        tenant_ids: form.tenant_ids,
        organization_id: form.organization_id || null,
        location_id: form.location_id || null,
        admin_user_id: form.role === "user" ? form.admin_user_id || null : null,
        distributor_user_id: form.role === "user" ? (isDistributor ? me!.id : form.distributor_user_id || null) : null,
        is_active: form.is_active,
      };
      if (isGlobalCustomerManager) {
        body.user_code = composeUserCode(form.user_code_year_range, form.user_code_suffix);
        body.tags = form.tags;
      }
      if (isGlobalCustomerManager && form.role === "admin") {
        body.storage_limit_gb = Number(form.storage_limit_gb);
      }
      return updateUser(editing.id, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users"] });
      setDialog(null);
      setEditing(null);
      setForm(emptyForm());
      setFormError(null);
    },
    onError: (e) => setFormError(errorDetail(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const resendInvitationMut = useMutation({
    mutationFn: (id: string) => resendInvitation(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
  const assignExistingOrgMut = useMutation({
    mutationFn: async () => {
      if (!assignOrgTarget) {
        throw new Error("No user selected.");
      }
      if (!assignOrgId) {
        throw new Error("Select an organization.");
      }
      if (!assignLocationId) {
        throw new Error("Select a location.");
      }
      return updateUser(assignOrgTarget.user.id, { organization_id: assignOrgId, location_id: assignLocationId });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users"] });
      setAssignOrgTarget(null);
      setAssignOrgId("");
      setAssignLocationId("");
      setAssignOrgError(null);
    },
    onError: (e) => setAssignOrgError(errorDetail(e)),
  });
  const createAndAssignOrgMut = useMutation({
    mutationFn: async () => {
      if (!assignOrgTarget) {
        throw new Error("No user selected.");
      }
      const orgName = newOrgForm.name.trim();
      if (!orgName) {
        throw new Error("Organization name is required.");
      }
      if (!newOrgForm.address_line_1.trim()) {
        throw new Error("Address line 1 is required.");
      }
      if (!newOrgForm.city.trim()) {
        throw new Error("City is required.");
      }
      if (!newOrgForm.state_id) {
        throw new Error("State is required.");
      }
      if (!/^\d{6}$/.test(newOrgForm.pincode.trim())) {
        throw new Error("Enter a six digit pincode.");
      }
      const org = await createOrganization({
        name: orgName,
        contact_email: newOrgForm.contact_email.trim() || null,
        phone: newOrgForm.phone.trim() || null,
        website: newOrgForm.website.trim() || null,
        is_active: true,
        tag_ids: newOrgForm.tag_ids,
        new_tags: newOrgForm.new_tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        first_location: {
          address_line_1: newOrgForm.address_line_1.trim(),
          address_line_2: newOrgForm.address_line_2.trim() || null,
          city: newOrgForm.city.trim(),
          state_id: newOrgForm.state_id,
          pincode: newOrgForm.pincode.trim(),
          is_primary: true,
          note: newOrgForm.note.trim() || null,
        },
      });
      const locations = await listOrganizationLocations(org.id);
      const primaryLocation = locations.items.find((location) => location.is_primary) ?? locations.items[0];
      if (!primaryLocation) {
        throw new Error("Created organization has no location.");
      }
      return updateUser(assignOrgTarget.user.id, { organization_id: org.id, location_id: primaryLocation.id });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["users"] });
      await qc.invalidateQueries({ queryKey: ["organizations"] });
      setAssignOrgTarget(null);
      setAssignOrgId("");
      setAssignLocationId("");
      setNewOrgForm(emptyNewOrganizationForm());
      setAssignOrgError(null);
    },
    onError: (e) => setAssignOrgError(errorDetail(e)),
  });
  const impersonateMut = useMutation({
    mutationFn: (id: string) => impersonateUser(id),
    onSuccess: async () => {
      qc.clear();
      navigate("/overview", { replace: true });
    },
  });

  const assignableAdmins = useMemo(() => {
    const directoryItems = directoryUsersQuery.data?.items ?? q.data?.items ?? [];
    if (!directoryItems.length) return [];
    if (me?.role === "super_admin") {
      return directoryItems.filter((u) => u.role === "admin");
    }
    if (me?.role === "admin" && me.id) {
      return directoryItems.filter((u) => u.role === "admin" && u.id === me.id);
    }
    return [];
  }, [directoryUsersQuery.data?.items, me?.id, me?.role, q.data?.items]);

  const assignableDistributors = useMemo(() => {
    const directoryItems = directoryUsersQuery.data?.items ?? q.data?.items ?? [];
    return directoryItems.filter((u) => u.role === "distributor");
  }, [directoryUsersQuery.data?.items, q.data?.items]);
  const availableTenants = tenantsQuery.data ?? me?.tenants ?? [];
  const availableOrganizations = useMemo(
    () => (organizationsQuery.data?.items ?? []).filter((org) => org.is_active),
    [organizationsQuery.data?.items],
  );
  const assignableOrganizations = useMemo(
    () => (assignOrganizationsQuery.data?.items ?? []).filter((org) => org.is_active),
    [assignOrganizationsQuery.data?.items],
  );
  const roleChoices = useMemo(() => {
    return ROLE_OPTIONS.filter((o) => canCreateRole(me ? { role: me.role ?? undefined, accountType: me.accountType ?? null } : null, o.value));
  }, [me]);
  const filterRoleOptions = useMemo(
    () => ROLE_OPTIONS.filter((option) => isGlobalCustomerManager || option.value !== "super_admin"),
    [isGlobalCustomerManager],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 350);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [search, roleFilter, effectiveWorkspaceFilter]);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const editableRoleChoices = useMemo(() => {
    if (!editing) return roleChoices;
    return ROLE_OPTIONS.filter((o) => canCreateRole(me ? { role: me.role ?? undefined, accountType: me.accountType ?? null } : null, o.value) || o.value === editing.role);
  }, [editing, me, roleChoices]);
  const customRoleChoices = useMemo(
    () => (rolesQuery.data?.items ?? []).filter((role) => !role.is_default && role.is_active),
    [rolesQuery.data?.items],
  );
  const roleSelectOptions = useMemo(() => {
    const defaultOptions = editableRoleChoices.map((role) => ({
      value: `default:${role.value}`,
      label: role.label,
      description: "Default role",
    }));
    const customOptions = customRoleChoices.map((role: RoleDto) => ({
      value: `custom:${role.id}`,
      label: role.name,
      description: role.description ?? "Custom workspace role",
    }));
    return [...defaultOptions, ...customOptions];
  }, [customRoleChoices, editableRoleChoices]);
  const roleSelectValue = form.role_id ? `custom:${form.role_id}` : `default:${form.role}`;
  const userCodeSuggestionPool = useMemo(
    () => directoryUsersQuery.data?.items ?? q.data?.items ?? [],
    [directoryUsersQuery.data?.items, q.data?.items],
  );
  const suggestedUserCodeSuffix = useMemo(
    () => nextAvailableUserCodeSuffix(userCodeSuggestionPool, form.user_code_year_range),
    [form.user_code_year_range, userCodeSuggestionPool],
  );

  const filteredUsers = q.data?.items ?? [];
  const totalUsers = q.data?.total ?? 0;
  const visibleUsers = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));
  const pageStart = totalUsers === 0 ? 0 : page * pageSize + 1;
  const pageEnd = totalUsers === 0 ? 0 : page * pageSize + visibleUsers;
  const hasActiveFilters =
    Boolean(searchInput.trim()) || Boolean(roleFilter) || (isGlobalCustomerManager && effectiveWorkspaceFilter !== "all");
  useEffect(() => {
    if (page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);
  if (!activeTenantId && !isAllWorkspacesScope) {
    return (
      <EnterprisePageShell hideHeader kicker="Access" title={isGlobalCustomerManager ? "Customers" : "Users & access"} description="Workspace context required.">
        <p className="p-4  text-caption text-muted">Loading session…</p>
      </EnterprisePageShell>
    );
  }

  if (!canAccess) {
    return (
      <EnterprisePageShell
        hideHeader
        kicker="Access"
        title={isGlobalCustomerManager ? "Customers" : "Users & access"}
        description="Your role does not include user management for this workspace."
      >
        <p className="p-4  text-caption text-muted">
          {isGlobalCustomerManager ? "Sign in with customer-management access to manage customers." : "Sign in as an admin or distributor to manage users."}
        </p>
      </EnterprisePageShell>
    );
  }

  const openCreate = () => {
    if (!canCreateUsers) return;
    setFormError(null);
    setEditing(null);
    setForm(
      emptyForm({
        user_code_suffix: nextAvailableUserCodeSuffix(
          directoryUsersQuery.data?.items ?? q.data?.items ?? [],
          defaultUserCodeYearRange(),
        ),
        role: isGlobalCustomerManager ? "admin" : isDistributor ? "user" : "user",
        role_id: "",
        auto_create_workspace: isGlobalCustomerManager,
        tenant_ids:
          isGlobalCustomerManager
            ? []
            : !isAllWorkspacesScope
            ? [effectiveWorkspaceFilter]
            : activeTenantId
              ? [activeTenantId]
              : [],
        organization_id: "",
        location_id: "",
        admin_user_id: "",
        distributor_user_id: isDistributor ? me?.id ?? "" : "",
        storage_limit_gb: isGlobalCustomerManager ? "2" : "",
      })
    );
    setDialog("create");
  };

  const openEdit = (u: UserDto) => {
    if (!canEditUsers || !canEditUser(me ? { id: me.id, role: me.role ?? undefined, accountType: me.accountType ?? null } : null, u)) return;
    const parsedUserCode = parseUserCode(u.user_code);
    setFormError(null);
    setEditing(u);
    setForm({
      email: u.email ?? "",
      name: u.name ?? "",
      phone: u.phone ?? "",
      user_code_year_range: parsedUserCode.yearRange,
      user_code_suffix: parsedUserCode.suffix,
      tags: u.tags ?? [],
      new_tag: "",
      role: ROLE_OPTIONS.some((o) => o.value === (u.role ?? "")) ? ((u.role ?? "user") as TenantUserRole) : "user",
      onboarding_mode: "invitation",
      password: "",
      confirm_password: "",
      password_acknowledged: false,
      role_id: ROLE_OPTIONS.some((o) => o.value === (u.role ?? ""))
        ? ""
        : (u.role_id ?? ""),
      tenant_ids: u.tenant_ids ?? (u.tenant_id ? [u.tenant_id] : []),
      auto_create_workspace: false,
      workspace_logo_file: null,
      workspace_company_name: "",
      organization_id: isGlobalCustomerManager ? u.organization_id ?? "" : me?.organizationId ?? u.organization_id ?? "",
      location_id: u.location_id ?? "",
      admin_user_id: u.admin_user_id ?? "",
      distributor_user_id: u.distributor_user_id ?? "",
      storage_limit_gb: u.role === "admin" ? String(u.storage_limit_gb ?? 2) : "",
      is_active: Boolean(u.is_active),
    });
    setDialog("edit");
  };

  const busy = createMut.isPending || updateMut.isPending;
  const userCodePreview = composeUserCode(form.user_code_year_range, form.user_code_suffix);

  return (
    <EnterprisePageShell
      hideHeader
      kicker="Access"
      title={isGlobalCustomerManager ? "Customers" : "Users & access"}
      description={isGlobalCustomerManager ? "Create, update, and deactivate customer workspace accounts." : "Create, update, and deactivate users. End users can be assigned to one admin, one distributor, or both."}
    >
      <div style={S.page}>
        {q.isLoading ? (
          <ListSurface>
            <ListSurfaceBody className="p-ds-4" aria-label="Loading users">
              {Array.from({ length: 8 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
            </ListSurfaceBody>
          </ListSurface>
        ) : q.isError ? (
          <p className="p-4  text-caption text-error">
            {q.error instanceof Error ? q.error.message : "Failed to load users"}
          </p>
        ) : (
          <ListSurface>
            <ListSurfaceHeader
              title={isGlobalCustomerManager ? "Customer directory" : "User directory"}
              description={isGlobalCustomerManager ? "Search, filter, and manage customer workspace accounts from one table view." : "Search, filter, and manage workspace accounts from one table view."}
              meta={
                <ListCount
                  total={totalUsers}
                  filtered={visibleUsers}
                  filteredLabel="On this page"
                  noun={totalUsers === 1 ? "user" : "users"}
                />
              }
              controls={
                <ListToolbar>
                  <ListToolbarGroup grow>
                    <input
                      className={tableHeaderSearchInputClass}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search email, user ID, tags, role, admin, distributor..."
                    />
                  </ListToolbarGroup>
                  <ListToolbarGroup>
                    <ThemedSelect
                      value={roleFilter}
                      onChange={(value) => setRoleFilter(value as "" | TenantUserRole)}
                      options={[
                        { value: "", label: "All roles" },
                        ...filterRoleOptions.map((o) => ({ value: o.value, label: o.label })),
                      ]}
                      ariaLabel="User role filter"
                      className="min-w-[180px]"
                      triggerClassName={SELECT_TRIGGER_CLASS}
                      menuClassName={SELECT_MENU_CLASS}
                      optionClassName="rounded-xl text-[13px]"
                    />
                  </ListToolbarGroup>
                  {hasActiveFilters ? (
                    <ToolbarIconButton
                      icon={FilterX}
                      label="Clear filters"
                      onClick={() => {
                        setSearch("");
                        setSearchInput("");
                        setRoleFilter("");
                        if (isGlobalCustomerManager) {
                        }
                      }}
                    />
                  ) : null}
                  {canCreateUsers ? (
                    <ToolbarIconButton icon={Plus} label="New user" variant="primary" onClick={openCreate} />
                  ) : null}
                </ListToolbar>
              }
            />
            <ListSurfaceBody className="overflow-x-auto">
              <table style={S.table}>
                <thead style={S.thead}>
                  <tr>
                    <th style={S.th}>Email</th>
                    {isGlobalCustomerManager ? <th style={S.th}>User ID</th> : null}
                    <th style={S.th}>Name</th>
                    <th style={S.th}>Role</th>
                    {isGlobalCustomerManager ? <th style={S.th}>Tags</th> : null}
                    <th style={S.th}>Workspaces</th>
                    <th style={S.th}>Organization</th>
                    <th style={S.th}>Assignments</th>
                    <th style={S.th}>Status</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length ? (
                    filteredUsers.map((u) => {
                      const canEdit = canEditUsers && canEditUser(me ? { id: me.id, role: me.role ?? undefined, accountType: me.accountType ?? null } : null, u);
                      const isProtectedUser = u.role === "super_admin";
                      const cannotDeleteSelf = me?.id === u.id;
                      const canImpersonate =
                        isGlobalCustomerManager &&
                        canEditUsers &&
                        u.id !== me.id &&
                        !isProtectedUser &&
                        u.account_type !== "platform" &&
                        u.is_active &&
                        u.account_status === "active";
                      const showDelete = canDeleteUsers && canEdit && !isProtectedUser && !cannotDeleteSelf;
                      const disableDelete = !showDelete || deleteMut.isPending;

                      return (
                        <tr key={u.id}>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{u.email}</div>
                          </td>
                          {isGlobalCustomerManager ? (
                            <td style={S.td}>
                              <span style={S.muted}>{u.user_code ?? "—"}</span>
                            </td>
                          ) : null}
                          <td style={S.td}>
                            <span style={S.muted}>{u.name ?? "—"}</span>
                          </td>
                          <td style={S.td}>
                            <span style={{ ...S.badge, ...tone(u.role) }}>{roleLabel(u.role)}</span>
                          </td>
                          {isGlobalCustomerManager ? (
                            <td style={S.td}>
                              {u.tags?.length ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                  {u.tags.map((tag) => (
                                    <span
                                      key={`${u.id}-${tag}`}
                                      style={{
                                        ...S.badge,
                                        background: "var(--muted-bg)",
                                        color: "var(--foreground)",
                                        borderColor: "var(--table-border)",
                                      }}
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={S.muted}>—</span>
                              )}
                            </td>
                          ) : null}
                          <td style={S.td}>
                            <span style={S.muted}>{tenantSummary(u)}</span>
                          </td>
                          <td style={S.td}>
                            <span style={S.muted}>{u.organization_name ?? "—"}</span>
                          </td>
                          <td style={S.td}>
                            {u.role === "user" ? (
                              <div style={{ ...S.muted, display: "grid", gap: "4px" }}>
                                <span>Admin: {u.admin_email ?? "—"}</span>
                                <span>Distributor: {u.distributor_email ?? "—"}</span>
                              </div>
                            ) : (
                              <span style={S.muted}>—</span>
                            )}
                          </td>
                          <td style={S.td}>
                            <div style={{ display: "grid", gap: "6px" }}>
                              <span style={{ ...S.badge, ...accountStatusTone(u.account_status) }}>
                                {accountStatusLabel(u)}
                              </span>
                              {u.account_status === "invited" && u.invitation_expires_at ? (
                                <span style={{ ...S.muted, fontSize: "11px" }}>
                                  Expires {new Date(u.invitation_expires_at).toLocaleDateString()}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td style={S.td}>
                            <div style={S.actions}>
                              {canEdit ? (
                                <button type="button" style={S.linkBtn} onClick={() => openEdit(u)} title="Edit user">
                                  <Pencil size={14} />
                                </button>
                              ) : null}
                              {canEdit && u.account_status === "invited" ? (
                                <button
                                  type="button"
                                  style={{
                                    ...S.linkBtn,
                                    ...(resendInvitationMut.isPending ? S.disabledBtn : {}),
                                  }}
                                  disabled={resendInvitationMut.isPending}
                                  onClick={() => resendInvitationMut.mutate(u.id)}
                                >
                                  <Mail size={14} />
                                  Resend
                                </button>
                              ) : null}
                              {canImpersonate ? (
                                <button
                                  type="button"
                                  style={{
                                    ...S.linkBtn,
                                    ...(impersonateMut.isPending ? S.disabledBtn : {}),
                                  }}
                                  disabled={impersonateMut.isPending}
                                  title="Login as user"
                                  onClick={() => {
                                    void (async () => {
                                      const confirmed = await dialogs.confirm({
                                        title: `Log in as ${u.email}?`,
                                        description: "You will switch into this user account for the current session.",
                                        confirmLabel: "Continue",
                                      });
                                      if (!confirmed) return;
                                      impersonateMut.mutate(u.id);
                                    })();
                                  }}
                                >
                                  <LogIn size={14} />
                                  {impersonateMut.isPending ? "Opening..." : ""}
                                </button>
                              ) : null}
                              {showDelete ? (
                                <button
                                  type="button"
                                  style={{
                                    ...S.linkBtn,
                                    ...S.dangerBtn,
                                    ...(disableDelete ? S.disabledBtn : {}),
                                  }}
                                  disabled={disableDelete}
                                  title="Delete user"
                                  onClick={() => {
                                    void (async () => {
                                      const confirmed = await dialogs.confirm({
                                        title: `Delete user ${u.email}?`,
                                        description: "This removes the user account from the workspace immediately.",
                                        confirmLabel: "Delete user",
                                        tone: "danger",
                                      });
                                      if (!confirmed) return;
                                      deleteMut.mutate(u.id);
                                    })();
                                  }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={isGlobalCustomerManager ? 10 : 8} style={{ padding: "24px" }}>
                        <EmptyState
                          title="No users match the current filters"
                          description="Adjust the search or role filter to bring users back into view."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </ListSurfaceBody>
            <ListSurfaceFooter
              left={
                <label className="flex items-center gap-ds-2">
                  <span className="text-caption text-muted">Rows per page</span>
                  <ThemedSelect
                    value={String(pageSize)}
                    onChange={(value) => setPageSize(Number(value))}
                    ariaLabel="Rows per page"
                    className="w-[5.5rem]"
                    triggerClassName="h-10 border-border bg-card hover:border-primary/20"
                    options={USERS_PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
                  />
                </label>
              }
              right={
                <>
                  <span className="text-caption text-muted">
                    {totalUsers === 0 ? "No pages" : `Showing ${pageStart}-${pageEnd} of ${totalUsers}`}
                  </span>
                  <div className="flex items-center gap-ds-2">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                      disabled={page === 0 || q.isFetching}
                      onClick={() => setPage((current) => Math.max(0, current - 1))}
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden />
                      Previous
                    </button>
                    <span className="text-caption tabular-nums text-muted">
                      Page {page + 1} / {totalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                      disabled={page + 1 >= totalPages || q.isFetching}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                </>
              }
            />
          </ListSurface>
        )}

        {dialog ? (
          <div
            style={S.modalBackdrop}
            role="dialog"
            aria-modal="true"
            onClick={() => {
              setDialog(null);
              setEditing(null);
              setFormError(null);
            }}
          >
            <div style={S.modal} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHead}>
                <h2 style={S.modalTitle}>{dialog === "create" ? "Create user" : "Edit user"}</h2>
                <p style={S.modalSub}>
                  {dialog === "create"
                    ? "Add a new workspace user and choose whether to invite them by email or set their password manually."
                    : "Update user details, role, and active status."}
                </p>
              </div>

              <div style={S.modalBody}>
                {dialog === "create" ? (
                  <label style={S.field}>
                    <span className="form-label form-label-required">Email</span>
                    <input
                      type="email"
                      className="ui-control"
                      style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      autoComplete="off"
                      required
                    />
                    <span className="form-helper">This becomes the sign-in identifier for the user.</span>
                  </label>
                ) : (
                  <div style={S.field}>
                    <span className="form-label">Email</span>
                    <div style={{ ...S.input, display: "flex", alignItems: "center", color: "var(--muted)" }}>
                      {form.email || editing?.email || "—"}
                    </div>
                  </div>
                )}

                <label style={S.field}>
                  <span className="form-label">Phone</span>
                  <input
                    type="tel"
                    className="ui-control"
                    style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                    value={form.phone}
                    maxLength={10}
                    onChange={(e) => {
                      const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setForm((f) => ({ ...f, phone: onlyDigits }));
                    }}
                  />
                  <span className="form-helper">Optional. Must be exactly 10 digits if provided.</span>
                </label>

                {dialog === "create" ? (
                  <div style={S.sectionCard}>
                    <span className="form-section-title">Onboarding</span>
                    <div style={{ display: "grid", gap: "10px" }}>
                      <label style={S.checkboxCard}>
                        <input
                          type="radio"
                          name="onboarding-mode"
                          style={S.checkbox}
                          checked={form.onboarding_mode === "invitation"}
                          onChange={() =>
                            setForm((f) => ({
                              ...f,
                              onboarding_mode: "invitation",
                              password: "",
                              confirm_password: "",
                              password_acknowledged: false,
                            }))
                          }
                        />
                        <span style={S.label}>Email invitation</span>
                        <span style={{ ...S.muted, fontSize: "12px" }}>
                          Send an invitation email so the user can set their own password.
                        </span>
                      </label>
                      <label style={S.checkboxCard}>
                        <input
                          type="radio"
                          name="onboarding-mode"
                          style={S.checkbox}
                          checked={form.onboarding_mode === "manual"}
                          onChange={() =>
                            setForm((f) => ({
                              ...f,
                              onboarding_mode: "manual",
                            }))
                          }
                        />
                        <span style={S.label}>Manual setup</span>
                        <span style={{ ...S.muted, fontSize: "12px" }}>
                          Set the password here and create the account without sending an invitation email.
                        </span>
                      </label>
                    </div>
                  </div>
                ) : null}

                <label style={S.field}>
                  <span className={`form-label ${dialog === "create" ? "form-label-required" : ""}`}>Display name</span>
                  <input
                    className="ui-control"
                    style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                  <span className="form-helper">
                    {dialog === "create"
                      ? form.onboarding_mode === "manual"
                        ? "Shown in the user list and available to share with the user during manual setup."
                        : "Shown in the user list and used in invitation emails."
                      : "Friendly name shown in tables, ownership labels, and activity logs."}
                  </span>
                </label>

                {isGlobalCustomerManager ? (
                  <div style={S.sectionCard}>
                    <label style={S.field}>
                      <span className="form-label form-label-required">User ID</span>
                      <div style={{ display: "grid", gridTemplateColumns: "88px minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
                        <div
                          className="ui-control"
                          style={{ ...S.input, display: "flex", alignItems: "center", color: "var(--muted)" }}
                        >
                          DT
                        </div>
                        <input
                          className="ui-control"
                          style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                          value={form.user_code_year_range}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              user_code_year_range: e.target.value.replace(/[^0-9-]/g, "").slice(0, 5),
                            }))
                          }
                          placeholder="26-27"
                        />
                        <input
                          className="ui-control"
                          style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                          value={form.user_code_suffix}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              user_code_suffix: e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 24),
                            }))
                          }
                          placeholder={suggestedUserCodeSuffix}
                        />
                      </div>
                      <span className="form-helper">Stored as {userCodePreview ?? `DT/${form.user_code_year_range}/${suggestedUserCodeSuffix}`} and must be unique.</span>
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <label style={S.field}>
                        <span className="form-label form-label-required">Role</span>
                        {dialog === "create" ? (
                          <div
                            className="ui-control"
                            style={{ ...S.input, display: "flex", alignItems: "center", color: "var(--foreground)" }}
                          >
                            Admin
                          </div>
                        ) : (
                          <ThemedSelect
                            value={roleSelectValue}
                            disabled={dialog === "edit" && (isDistributor || editing?.id === me?.id)}
                            onChange={(value) => {
                              const [kind, id] = value.split(":");
                              if (kind === "custom") {
                                setForm((f) => ({
                                  ...f,
                                  role_id: id ?? "",
                                  auto_create_workspace: false,
                                }));
                                return;
                              }
                              const nextRole = id as TenantUserRole;
                              setForm((f) => ({
                                ...f,
                                role: nextRole,
                                role_id: "",
                                auto_create_workspace: false,
                                admin_user_id: nextRole === "user" ? f.admin_user_id : "",
                                distributor_user_id:
                                  nextRole === "user" ? (isDistributor ? me?.id ?? "" : f.distributor_user_id) : "",
                                storage_limit_gb: nextRole === "admin" ? f.storage_limit_gb || "2" : "",
                              }));
                            }}
                            options={roleSelectOptions}
                            ariaLabel="User role"
                            triggerClassName={SELECT_TRIGGER_CLASS}
                            menuClassName={SELECT_MENU_CLASS}
                            optionClassName="rounded-xl text-[13px]"
                          />
                        )}
                        {dialog === "create" ? (
                          <span className="form-helper-disabled">Super admins create workspace admin accounts from this flow.</span>
                        ) : dialog === "edit" && (isDistributor || editing?.id === me?.id) ? (
                          <span className="form-helper-disabled">Role changes are locked for your own account and distributor-managed users.</span>
                        ) : (
                          <span className="form-helper">Choose whether this user manages workspaces or operates within one.</span>
                        )}
                      </label>

                      <label style={S.field}>
                        <span className={`form-label ${form.role === "admin" ? "form-label-required" : ""}`}>Storage limit (GB)</span>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          disabled={form.role !== "admin"}
                          className="ui-control"
                          style={{
                            ...S.input,
                            border: undefined,
                            background: undefined,
                            outline: undefined,
                            boxShadow: undefined,
                            opacity: form.role === "admin" ? 1 : 0.6,
                          }}
                          value={form.storage_limit_gb}
                          onChange={(e) => setForm((f) => ({ ...f, storage_limit_gb: e.target.value.replace(/\D/g, "") }))}
                          required={form.role === "admin"}
                        />
                        <span className="form-helper">Applies to telemetry, alerts, and backup files for data loggers assigned to this admin.</span>
                      </label>
                    </div>

                    <div style={S.field}>
                      <span className="form-label">Tags</span>
                      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "10px" }}>
                        <input
                          className="ui-control"
                          style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                          value={form.new_tag}
                          onChange={(e) => setForm((f) => ({ ...f, new_tag: e.target.value }))}
                          placeholder="Private, Reseller, Railway"
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") {
                              return;
                            }
                            e.preventDefault();
                            const nextTag = normalizeUserTag(form.new_tag);
                            if (!nextTag || form.tags.some((tag) => tag.toLowerCase() === nextTag.toLowerCase())) {
                              return;
                            }
                            setForm((f) => ({ ...f, tags: [...f.tags, nextTag], new_tag: "" }));
                          }}
                        />
                        <button
                          type="button"
                          style={S.btn}
                          onClick={() => {
                            const nextTag = normalizeUserTag(form.new_tag);
                            if (!nextTag || form.tags.some((tag) => tag.toLowerCase() === nextTag.toLowerCase())) {
                              return;
                            }
                            setForm((f) => ({ ...f, tags: [...f.tags, nextTag], new_tag: "" }));
                          }}
                        >
                          Add
                        </button>
                      </div>
                      {form.tags.length ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {form.tags.map((tag) => (
                            <span
                              key={tag}
                              style={{
                                ...S.badge,
                                background: "color-mix(in srgb, var(--btn-primary-to) 12%, var(--card))",
                                color: "var(--btn-primary-to)",
                                borderColor: "color-mix(in srgb, var(--btn-primary-to) 28%, transparent)",
                              }}
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((item) => item !== tag) }))}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  padding: 0,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  color: "inherit",
                                }}
                                aria-label={`Remove ${tag}`}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="form-helper">Add searchable tags for this user.</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <label style={S.field}>
                    <span className="form-label form-label-required">Role</span>
                    <ThemedSelect
                      value={roleSelectValue}
                      disabled={dialog === "edit" && (isDistributor || editing?.id === me?.id)}
                      onChange={(value) => {
                        const [kind, id] = value.split(":");
                        if (kind === "custom") {
                          setForm((f) => ({
                            ...f,
                            role_id: id ?? "",
                            auto_create_workspace: false,
                          }));
                          return;
                        }
                        const nextRole = id as TenantUserRole;
                        setForm((f) => ({
                          ...f,
                          role: nextRole,
                          role_id: "",
                          auto_create_workspace: dialog === "create" && isGlobalCustomerManager && nextRole === "admin",
                          admin_user_id: nextRole === "user" ? f.admin_user_id : "",
                          distributor_user_id:
                            nextRole === "user" ? (isDistributor ? me?.id ?? "" : f.distributor_user_id) : "",
                          storage_limit_gb: isGlobalCustomerManager && nextRole === "admin" ? f.storage_limit_gb || "2" : "",
                        }));
                      }}
                      options={roleSelectOptions}
                      ariaLabel="User role"
                      triggerClassName={SELECT_TRIGGER_CLASS}
                      menuClassName={SELECT_MENU_CLASS}
                      optionClassName="rounded-xl text-[13px]"
                    />
                    {dialog === "edit" && (isDistributor || editing?.id === me?.id) ? (
                      <span className="form-helper-disabled">Role changes are locked for your own account and distributor-managed users.</span>
                    ) : (
                      <span className="form-helper">Choose whether this user manages workspaces or operates within one.</span>
                    )}
                  </label>
                )}

                {autoCreateWorkspaceEnabled ? (
                  <>
                    <label style={S.checkboxCard}>
                      <input
                        type="checkbox"
                        style={S.checkbox}
                        checked={form.auto_create_workspace}
                        onChange={(e) => setForm((f) => ({ ...f, auto_create_workspace: e.target.checked, workspace_logo_file: e.target.checked ? f.workspace_logo_file : null }))}
                      />
                      <span style={S.label}>Create workspace automatically</span>
                      <span style={{ ...S.muted, fontSize: "12px" }}>
                        Creates {form.name.trim() || "this admin"}'s workspace and makes this admin the owner.
                      </span>
                    </label>
                    {form.auto_create_workspace ? (
                      <div style={{ ...S.sectionCard, gap: "10px" }}>
                        <label style={S.field}>
                          <span className="form-label">Company name</span>
                          <input
                            className="ui-control"
                            style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                            value={form.workspace_company_name}
                            onChange={(event) => setForm((f) => ({ ...f, workspace_company_name: event.target.value }))}
                            placeholder={form.name.trim() || "Company name"}
                          />
                          <span className="form-helper">Shown in the sidebar for this workspace’s members. Defaults to the admin name if left blank.</span>
                        </label>
                        <div>
                          <span className="form-section-title">New workspace logo</span>
                          <span className="form-helper">Optional. Recommended: 256 × 256 px square, PNG, JPG, or WebP, up to 2 MB.</span>
                        </div>
                        {form.workspace_logo_file ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "13px" }}>{form.workspace_logo_file.name}</span>
                            <button type="button" style={S.btn} onClick={() => setForm((f) => ({ ...f, workspace_logo_file: null }))}>Remove</button>
                          </div>
                        ) : (
                          <label style={{ ...S.btn, width: "fit-content", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                            <Upload size={15} /> Upload logo
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              style={{ display: "none" }}
                              onChange={(event) => {
                                const file = event.target.files?.[0] ?? null;
                                if (file) setForm((f) => ({ ...f, workspace_logo_file: file }));
                                event.currentTarget.value = "";
                              }}
                            />
                          </label>
                        )}
                      </div>
                    ) : null}
                  </>
                ) : null}

                <div style={S.sectionCard}>
                  <span className="form-section-title">Workspace assignments</span>
                  <div
                    style={{
                      border: formError === "Select at least one workspace." ? "1px solid #fca5a5" : "1px solid var(--table-border)",
                      borderRadius: "12px",
                      padding: "12px 14px",
                      display: "grid",
                      gap: "10px",
                      background: "var(--card)",
                    }}
                  >
                    {!workspaceAssignmentRequired ? (
                      <span style={{ fontSize: "12px", color: "var(--muted)" }}>
                        A new workspace will be created after this admin is saved.
                      </span>
                    ) : availableTenants.length ? (
                      availableTenants.map((tenant) => {
                        const checked = form.tenant_ids.includes(tenant.id);
                        return (
                          <label
                            key={tenant.id}
                            style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", color: "#374151" }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  role_id: "",
                                  tenant_ids: e.target.checked
                                    ? [...prev.tenant_ids, tenant.id]
                                    : prev.tenant_ids.filter((id) => id !== tenant.id),
                                }))
                              }
                            />
                            <span style={{ fontWeight: 600 }}>{tenant.name}</span>
                            <span style={{ color: "var(--muted)", fontSize: "12px" }}>
                              {tenant.domain ?? tenant.id}
                            </span>
                          </label>
                        );
                      })
                    ) : (
                      <span style={{ fontSize: "12px", color: "var(--muted)" }}>No accessible workspaces found.</span>
                    )}
                  </div>
                  {formError === "Select at least one workspace." ? (
                    <span className="form-error">Select at least one workspace.</span>
                  ) : (
                    <span className="form-helper">
                      {workspaceAssignmentRequired
                        ? "Users need at least one workspace assignment before they can access data."
                        : "The new workspace will be named from the admin display name and owned by this user."}
                    </span>
                  )}
                </div>

                {dialog === "edit" ? (
                  <div style={S.sectionCard}>
                    <span className="form-section-title">Organization and location</span>
                    {isGlobalCustomerManager ? (
                      <label style={S.field}>
                        <span className="form-label">Organization assignment</span>
                        <ThemedSelect
                          value={form.organization_id}
                          onChange={(value) => setForm((f) => ({ ...f, organization_id: value, location_id: "" }))}
                          options={[
                            { value: "", label: "None" },
                            ...availableOrganizations.map((org) => ({
                              value: org.id,
                              label: org.name,
                              description: org.contact_email ?? org.phone ?? undefined,
                            })),
                          ]}
                          ariaLabel="Assigned organization"
                          triggerClassName={SELECT_TRIGGER_CLASS}
                          menuClassName={SELECT_MENU_CLASS}
                          optionClassName="rounded-xl text-[13px]"
                        />
                        <span className="form-helper">Super admins can move users between organizations.</span>
                      </label>
                    ) : (
                      <div style={S.field}>
                        <span className="form-label">Organization assignment</span>
                        <div style={{ ...S.input, display: "flex", alignItems: "center", color: "var(--muted)" }}>
                          {me?.organizationName || editing?.organization_name || "Your organization"}
                        </div>
                        <span className="form-helper-disabled">Organization is fixed to your account scope.</span>
                      </div>
                    )}

                    {editOrganizationId ? (
                      <label style={S.field}>
                        <span className="form-label">Location assignment</span>
                        <ThemedSelect
                          value={form.location_id}
                          onChange={(value) => setForm((f) => ({ ...f, location_id: value }))}
                          options={[
                            { value: "", label: editLocationsQuery.isLoading ? "Loading locations..." : "Select location" },
                            ...(editLocationsQuery.data?.items ?? []).map((location) => ({
                              value: location.id,
                              label: location.name,
                              description: [location.city, location.state_name, location.pincode].filter(Boolean).join(", ") || undefined,
                            })),
                          ]}
                          ariaLabel="Assigned location"
                          triggerClassName={SELECT_TRIGGER_CLASS}
                          menuClassName={SELECT_MENU_CLASS}
                          optionClassName="rounded-xl text-[13px]"
                        />
                        <span className="form-helper">Controls which organization location this user can manage.</span>
                      </label>
                    ) : (
                      <span className="form-helper-disabled">Select an organization before assigning a location.</span>
                    )}
                  </div>
                ) : null}

                {form.role === "user" && !isDistributor ? (
                  <>
                  {assignableAdmins.length ? (
                    <label style={S.field}>
                      <span className="form-label">Assigned admin</span>
                      <ThemedSelect
                        value={form.admin_user_id}
                        onChange={(value) => setForm((f) => ({ ...f, admin_user_id: value }))}
                        options={[
                          { value: "", label: "None" },
                          ...assignableAdmins
                            .filter((p) => dialog === "create" || p.id !== editing?.id)
                            .map((p) => ({
                              value: p.id,
                              label: p.email,
                              description: roleLabel(p.role),
                            })),
                        ]}
                        ariaLabel="Assigned admin user"
                        triggerClassName={SELECT_TRIGGER_CLASS}
                        menuClassName={SELECT_MENU_CLASS}
                        optionClassName="rounded-xl text-[13px]"
                      />
                      <span className="form-helper">Optional escalation owner for standard users.</span>
                    </label>
                  ) : null}
                  <label style={S.field}>
                    <span className="form-label">Assigned distributor</span>
                    <ThemedSelect
                      value={form.distributor_user_id}
                      onChange={(value) => setForm((f) => ({ ...f, distributor_user_id: value }))}
                      options={[
                        { value: "", label: "None" },
                        ...assignableDistributors
                          .filter((p) => dialog === "create" || p.id !== editing?.id)
                          .map((p) => ({
                            value: p.id,
                            label: p.email,
                            description: roleLabel(p.role),
                          })),
                      ]}
                      ariaLabel="Assigned distributor user"
                      triggerClassName={SELECT_TRIGGER_CLASS}
                      menuClassName={SELECT_MENU_CLASS}
                      optionClassName="rounded-xl text-[13px]"
                    />
                    <span className="form-helper">Use a distributor assignment when this user should roll up under a distributor account.</span>
                  </label>
                  </>
                ) : null}

                {form.role === "user" && isDistributor ? (
                  <>
                    <div style={{ ...S.checkboxCard, fontSize: "12px", color: "var(--muted)" }}>
                      Distributor assignment is fixed to you ({me?.email}).
                    </div>
                    <span className="form-helper-disabled">Distributor assignment is managed automatically for your account.</span>
                  </>
                ) : null}

                {dialog === "create" && form.onboarding_mode === "manual" ? (
                  <div style={S.sectionCard}>
                    <span className="form-section-title">Manual password setup</span>
                    <label style={S.field}>
                      <span className="form-label form-label-required">Password</span>
                      <input
                        type="password"
                        className="ui-control"
                        style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                        value={form.password}
                        minLength={8}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      />
                      <span className="form-helper">Use at least 8 characters.</span>
                    </label>
                    <label style={S.field}>
                      <span className="form-label form-label-required">Confirm password</span>
                      <input
                        type="password"
                        className="ui-control"
                        style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                        value={form.confirm_password}
                        minLength={8}
                        onChange={(e) => setForm((f) => ({ ...f, confirm_password: e.target.value }))}
                      />
                    </label>
                    <label style={S.checkboxCard}>
                      <input
                        type="checkbox"
                        style={S.checkbox}
                        checked={form.password_acknowledged}
                        onChange={(e) => setForm((f) => ({ ...f, password_acknowledged: e.target.checked }))}
                      />
                      <span style={S.label}>I have noted down the password</span>
                    </label>
                  </div>
                ) : null}

                {dialog === "create" ? (
                  <div style={{ ...S.sectionCard, gap: "8px" }}>
                    <span className="form-section-title">Access setup</span>
                    <span className="form-helper">
                      {form.onboarding_mode === "manual"
                        ? "After save, the account will be created with this password and no invitation email will be sent."
                        : "After save, the user will receive an email invitation to set their own password. Invitation links stay valid for 7 days."}
                    </span>
                  </div>
                ) : null}

                <label style={S.checkboxCard}>
                  <input
                    type="checkbox"
                    style={S.checkbox}
                    checked={form.is_active}
                    disabled={dialog === "edit" && isDistributor && editing?.id === me?.id}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                  <span style={S.label}>Allow access after setup</span>
                </label>
                {dialog === "edit" && isDistributor && editing?.id === me?.id ? (
                  <span className="form-helper-disabled">You cannot deactivate your own distributor account from this screen.</span>
                ) : null}
              </div>

              <div style={S.modalFooter}>
                <div style={{ marginRight: "auto", minWidth: 0, flex: "1 1 220px" }}>
                  {formError ? <div style={S.error}>{formError}</div> : null}
                </div>
                <button
                  type="button"
                  style={S.btn}
                  onClick={() => {
                    setDialog(null);
                    setEditing(null);
                    setFormError(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={S.btnPrimary}
                  disabled={busy}
                  onClick={() => {
                    setFormError(null);
                    if (isGlobalCustomerManager && form.role === "admin" && (!form.storage_limit_gb || Number(form.storage_limit_gb) < 1)) {
                      setFormError("Storage limit is required for admin users.");
                      return;
                    }

                    if (dialog === "create") {
                      if (workspaceAssignmentRequired && form.tenant_ids.length === 0) {
                        setFormError("Select at least one workspace.");
                        return;
                      }
                    if (!form.email.trim()) {
                      setFormError("Email is required.");
                      return;
                    }
                    if (isGlobalCustomerManager) {
                      if (!/^\d{2}-\d{2}$/.test(form.user_code_year_range.trim())) {
                        setFormError("User ID year range must look like 26-27.");
                        return;
                      }
                      if (!/^[A-Za-z0-9]+$/.test(form.user_code_suffix.trim())) {
                        setFormError("User ID suffix must be alphanumeric.");
                        return;
                      }
                    }
                    if (!form.name.trim()) {
                      setFormError("Display name is required.");
                      return;
                    }
                    if (form.onboarding_mode === "manual") {
                      if (form.password.length < 8) {
                        setFormError("Manual password must be at least 8 characters.");
                        return;
                      }
                      if (form.password !== form.confirm_password) {
                        setFormError("Password confirmation does not match.");
                        return;
                      }
                      if (!form.password_acknowledged) {
                        setFormError("Confirm that you have noted down the password.");
                        return;
                      }
                    }
                    if (
                        form.role === "user" &&
                        !form.admin_user_id &&
                        !(isDistributor ? me?.id : form.distributor_user_id)
                      ) {
                        setFormError("Select at least one assignment for this user.");
                        return;
                      }
                      if (form.phone && !/^\d{10}$/.test(form.phone)) {
                        setFormError("Phone must be exactly 10 digits.");
                        return;
                      }
                      createMut.mutate();
                      return;
                    }

                    if (dialog === "edit" && editing) {
                      if (workspaceAssignmentRequired && form.tenant_ids.length === 0) {
                        setFormError("Select at least one workspace.");
                        return;
                      }
                      if (isGlobalCustomerManager) {
                        if (!/^\d{2}-\d{2}$/.test(form.user_code_year_range.trim())) {
                          setFormError("User ID year range must look like 26-27.");
                          return;
                        }
                        if (!/^[A-Za-z0-9]+$/.test(form.user_code_suffix.trim())) {
                          setFormError("User ID suffix must be alphanumeric.");
                          return;
                        }
                      }
                      if (
                        form.role === "user" &&
                        !form.admin_user_id &&
                        !(isDistributor ? me?.id : form.distributor_user_id)
                      ) {
                        setFormError("Select at least one assignment for this user.");
                        return;
                      }
                      if (
                        form.role !== "super_admin" &&
                        (form.is_active || form.organization_id) &&
                        (!form.organization_id || !form.location_id)
                      ) {
                        setFormError("Select an organization and location for this user.");
                        return;
                      }
                      if (form.phone && !/^\d{10}$/.test(form.phone)) {
                        setFormError("Phone must be exactly 10 digits.");
                        return;
                      }
                      updateMut.mutate();
                    }
                  }}
                >
                                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {assignOrgTarget ? (
          <div
            style={S.modalBackdrop}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ ...S.modal, maxWidth: "680px" }} onClick={(e) => e.stopPropagation()}>
              <div style={S.modalHead}>
                <h2 style={S.modalTitle}>Assign organization</h2>
                <p style={S.modalSub}>
                  {assignOrgTarget.user.email} was created. Assign an organization and location.
                </p>
              </div>

              <div style={S.modalBody}>
                {assignOrgError ? <div style={S.error}>{assignOrgError}</div> : null}

                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    style={{
                      ...(assignOrgMode === "existing" ? S.btnPrimary : S.btn),
                      minHeight: "36px",
                    }}
                    onClick={() => {
                      setAssignOrgMode("existing");
                      setAssignOrgError(null);
                    }}
                  >
                    Existing organization
                  </button>
                  {isGlobalCustomerManager ? (
                    <button
                      type="button"
                      style={{
                        ...(assignOrgMode === "new" ? S.btnPrimary : S.btn),
                        minHeight: "36px",
                      }}
                      onClick={() => {
                        setAssignOrgMode("new");
                        setAssignOrgError(null);
                      }}
                    >
                      Create new
                    </button>
                  ) : null}
                </div>

                {assignOrgMode === "existing" ? (
                  <>
                    <label style={S.field}>
                      <span className="form-label form-label-required">Organization</span>
                      <ThemedSelect
                        value={assignOrgId}
                        onChange={(value) => {
                          setAssignOrgId(value);
                          setAssignLocationId("");
                        }}
                        options={[
                          { value: "", label: assignOrganizationsQuery.isLoading ? "Loading organizations..." : "Select organization" },
                          ...assignableOrganizations.map((org) => ({
                            value: org.id,
                            label: org.name,
                            description: org.contact_email ?? org.phone ?? undefined,
                          })),
                        ]}
                        ariaLabel="Organization to assign"
                        triggerClassName={SELECT_TRIGGER_CLASS}
                        menuClassName={SELECT_MENU_CLASS}
                        optionClassName="rounded-xl text-[13px]"
                      />
                      <span className="form-helper">Showing active organizations available to your account.</span>
                    </label>
                    {assignOrgId ? (
                      <label style={S.field}>
                        <span className="form-label form-label-required">Location</span>
                        <ThemedSelect
                          value={assignLocationId}
                          onChange={setAssignLocationId}
                          options={[
                            { value: "", label: assignLocationsQuery.isLoading ? "Loading locations..." : "Select location" },
                            ...(assignLocationsQuery.data?.items ?? []).map((location) => ({
                              value: location.id,
                              label: location.name,
                              description: [location.city, location.state_name, location.pincode].filter(Boolean).join(", ") || undefined,
                            })),
                          ]}
                          ariaLabel="Location to assign"
                          triggerClassName={SELECT_TRIGGER_CLASS}
                          menuClassName={SELECT_MENU_CLASS}
                          optionClassName="rounded-xl text-[13px]"
                        />
                      </label>
                    ) : null}
                  </>
                ) : (
                  <div style={{ display: "grid", gap: "14px" }}>
                    <label style={S.field}>
                      <span className="form-label form-label-required">Name</span>
                      <input
                        className="ui-control"
                        style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                        value={newOrgForm.name}
                        onChange={(e) => setNewOrgForm((prev) => ({ ...prev, name: e.target.value }))}
                      />
                    </label>
                    <label style={S.field}>
                      <span className="form-label">Contact email</span>
                      <input
                        type="email"
                        className="ui-control"
                        style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                        value={newOrgForm.contact_email}
                        onChange={(e) => setNewOrgForm((prev) => ({ ...prev, contact_email: e.target.value }))}
                      />
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <label style={S.field}>
                        <span className="form-label">Phone</span>
                        <input className="ui-control" style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }} value={newOrgForm.phone} onChange={(e) => setNewOrgForm((prev) => ({ ...prev, phone: e.target.value }))} />
                      </label>
                      <label style={S.field}>
                        <span className="form-label">Website</span>
                        <input className="ui-control" style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }} value={newOrgForm.website} onChange={(e) => setNewOrgForm((prev) => ({ ...prev, website: e.target.value }))} />
                      </label>
                    </div>
                    <div style={{ display: "grid", gap: "10px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--foreground)" }}>Tags</div>
                      {(organizationTagsQuery.data ?? []).length ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                          {(organizationTagsQuery.data ?? []).map((tag) => {
                            const checked = newOrgForm.tag_ids.includes(tag.id);
                            return (
                              <label
                                key={tag.id}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  minHeight: "34px",
                                  padding: "0 10px",
                                  borderRadius: "999px",
                                  border: checked ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                                  background: checked ? "#eff6ff" : "#ffffff",
                                  color: checked ? "#1d4ed8" : "#475569",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    setNewOrgForm((prev) => ({
                                      ...prev,
                                      tag_ids: e.target.checked
                                        ? [...prev.tag_ids, tag.id]
                                        : prev.tag_ids.filter((id) => id !== tag.id),
                                    }))
                                  }
                                />
                                {tag.name}
                              </label>
                            );
                          })}
                        </div>
                      ) : null}
                      <label style={S.field}>
                        <span className="form-label">Create tags</span>
                        <input
                          className="ui-control"
                          style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }}
                          value={newOrgForm.new_tags}
                          onChange={(e) => setNewOrgForm((prev) => ({ ...prev, new_tags: e.target.value }))}
                          placeholder="Installation partner, Key account"
                        />
                        <span className="form-helper">Separate multiple new tags with commas.</span>
                      </label>
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--foreground)" }}>Primary location</div>
                    <label style={S.field}>
                      <span className="form-label form-label-required">Address line 1</span>
                      <input className="ui-control" style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }} value={newOrgForm.address_line_1} onChange={(e) => setNewOrgForm((prev) => ({ ...prev, address_line_1: e.target.value }))} />
                    </label>
                    <label style={S.field}>
                      <span className="form-label">Address line 2</span>
                      <input className="ui-control" style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }} value={newOrgForm.address_line_2} onChange={(e) => setNewOrgForm((prev) => ({ ...prev, address_line_2: e.target.value }))} />
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <label style={S.field}>
                        <span className="form-label form-label-required">City</span>
                        <input className="ui-control" style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }} value={newOrgForm.city} onChange={(e) => setNewOrgForm((prev) => ({ ...prev, city: e.target.value }))} />
                      </label>
                      <label style={S.field}>
                        <span className="form-label form-label-required">State</span>
                        <ThemedSelect
                          value={newOrgForm.state_id}
                          onChange={(value) => setNewOrgForm((prev) => ({ ...prev, state_id: value }))}
                          options={[
                            { value: "", label: "Select state" },
                            ...(statesQuery.data ?? []).map((state) => ({ value: state.id, label: state.name, description: state.code })),
                          ]}
                          ariaLabel="Primary location state"
                          triggerClassName={SELECT_TRIGGER_CLASS}
                          menuClassName={SELECT_MENU_CLASS}
                          optionClassName="rounded-xl text-[13px]"
                        />
                      </label>
                    </div>
                    <label style={S.field}>
                      <span className="form-label form-label-required">Pincode</span>
                      <input className="ui-control" style={{ ...S.input, border: undefined, background: undefined, outline: undefined, boxShadow: undefined }} value={newOrgForm.pincode} inputMode="numeric" maxLength={6} onChange={(e) => setNewOrgForm((prev) => ({ ...prev, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))} />
                    </label>
                    <label style={S.field}>
                      <span className="form-label">Note</span>
                      <textarea className="ui-control" value={newOrgForm.note} onChange={(e) => setNewOrgForm((prev) => ({ ...prev, note: e.target.value }))} rows={3} />
                    </label>
                    <span className="form-helper">
                      New organizations are created active by default.
                    </span>
                  </div>
                )}
              </div>

              <div style={S.modalFooter}>
                <button
                  type="button"
                  style={S.btnPrimary}
                  disabled={assignExistingOrgMut.isPending || createAndAssignOrgMut.isPending}
                  onClick={() => {
                    setAssignOrgError(null);
                    if (assignOrgMode === "existing") {
                      if (!assignOrgId) {
                        setAssignOrgError("Select an organization.");
                        return;
                      }
                      if (!assignLocationId) {
                        setAssignOrgError("Select a location.");
                        return;
                      }
                      assignExistingOrgMut.mutate();
                      return;
                    }
                    if (!newOrgForm.name.trim()) {
                      setAssignOrgError("Organization name is required.");
                      return;
                    }
                    if (!newOrgForm.address_line_1.trim() || !newOrgForm.city.trim() || !newOrgForm.state_id || !/^\d{6}$/.test(newOrgForm.pincode.trim())) {
                      setAssignOrgError("Complete the primary location fields.");
                      return;
                    }
                    createAndAssignOrgMut.mutate();
                  }}
                >
                  {assignExistingOrgMut.isPending || createAndAssignOrgMut.isPending ? "Assigning..." : "Assign organization"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </EnterprisePageShell>
  );
}
